import React, { useRef, useState, useEffect } from 'react';
import CommonModal from '../../../extras/commonModal';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Chip,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
} from '@material-ui/core';
import { Add as AddIcon, Close as CloseIcon } from '@material-ui/icons';
import { toast } from 'react-toastify';
import {
  createNewScrapeRequest,
  getAllKeywords,
} from '../../../services/apiService';
import LoadingCircleIcon from '../../../icons/LoadingCircle';

const CreateRequest = ({
  openCreateModal,
  setOpenCreateModal,
  onGridReady,
  onRequestCreated,
  gridApi,
  refetchData,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [keywordOptions, setKeywordOptions] = useState([]);
  const [keywordLoading, setKeywordLoading] = useState(false);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [keywordSearch, setKeywordSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const isMountedRef = useRef(null);
  const debounceTimeout = useRef(null);

  const initialFormState = {
    inputId: '',
    requestName: '',
    platform: 'LinkedIn',
    lastRun: '',
    type: 'Cron',
    frequency: '',
  };

  const [formValues, setFormValues] = useState(initialFormState);

  const [errors, setErrors] = useState({});

  const fetchKeywords = async (searchTerm = '') => {
    setKeywordLoading(true);
    try {
      const response = await getAllKeywords();

      let mappedKeywords = [];

      if (response && response.data && Array.isArray(response.data)) {
        mappedKeywords = response.data.map((item) => ({
          id: item?.id,
          name: item?.keyword,
          value: item?.keyword?.toLowerCase().replace(/\s+/g, '-'),
          platform: item?.platform,
        }));
      }

      if (searchTerm) {
        mappedKeywords = mappedKeywords.filter((keyword) =>
          keyword.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      setKeywordOptions(mappedKeywords);
    } catch (error) {
      console.error('Failed to fetch keywords:', error);
      toast.error('Failed to load keywords');
      setKeywordOptions([]); // Set empty array on error
    } finally {
      setKeywordLoading(false);
    }
  };

  // Load initial keywords when component mounts
  useEffect(() => {
    if (openCreateModal) {
      fetchKeywords();
    }
  }, [openCreateModal]);

  // Handle regular input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // Handle keyword selection/addition
  const handleAddKeyword = (keyword) => {
    if (!selectedKeywords.find((k) => k.id === keyword.id)) {
      setSelectedKeywords([...selectedKeywords, keyword]);
      setKeywordSearch('');
      setShowSuggestions(false);
    }
  };

  // Handle adding new keyword
  const handleAddNewKeyword = () => {
    if (
      keywordSearch.trim() &&
      !selectedKeywords.find(
        (k) => k.name.toLowerCase() === keywordSearch.toLowerCase() && k.platform === formValues.platform
      )
    ) {
      const newKeyword = {
        id: `new-${Date.now()}`,
        name: keywordSearch.trim(),
        value: keywordSearch.toLowerCase().replace(/\s+/g, '-'),
        platform: formValues.platform,
        isNew: true,
      };
      setSelectedKeywords([...selectedKeywords, newKeyword]);
      setKeywordSearch('');
      setShowSuggestions(false);
    }
  };

  // Handle removing keyword
  const handleRemoveKeyword = (keywordToRemove) => {
    setSelectedKeywords(
      selectedKeywords.filter((k) => k.id !== keywordToRemove.id)
    );
  };

  // Handle keyword search input
  const handleKeywordSearchChange = (e) => {
    const value = e.target.value;
    setKeywordSearch(value);
    setShowSuggestions(value.length > 0);
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      if (value.length > 0) {
        fetchKeywords(value);
      }
    }, 500);
  };

  // Validate the form
  const validateForm = () => {
    const newErrors = {};

    // Check required fields
    if (!formValues.platform) newErrors.platform = 'Platform is required';
    if (!formValues.requestName)
      newErrors.requestName = 'Request Name is required';
    if (!formValues.type) newErrors.type = 'Type is required';

    // Frequency validation
    if (formValues.type === 'Cron' && !formValues.frequency) {
      newErrors.frequency = 'Frequency is required for Cron type';
    }

    // Keywords validation
    if (!selectedKeywords || selectedKeywords.length === 0) {
      newErrors.keywords = 'At least one keyword is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  async function createNewRequest(payload) {
    console.log(payload, 'payload in createNewRequest');
    try {
      const response = await createNewScrapeRequest(payload );
      toast.success('Request created successfully');
      return response;
    } catch (error) {
      toast.error('Failed to create request');
      console.error(error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }

  // Handle form submission
  const handleCreateRequest = async () => {
    if (!validateForm()) return;
    setIsLoading(true);

    // Prepare keyword data - separate existing and new keywords
    const existingKeywords = selectedKeywords.filter((k) => !k.isNew);
    const newKeywords = selectedKeywords.filter((k) => k.isNew);
    const requestIds = [
      ...existingKeywords.map((k) => k.id),
      ...newKeywords.map((k) => k.name),
    ];

    const requestData = {
      requestName: formValues.requestName,
      requestIds: requestIds, // IDs for existing keywords
      platform: formValues.platform,
      type: formValues.type,
      frequency: formValues.type === 'Cron' ? formValues.frequency : 'one-time',
    };

    const response = await createNewRequest(requestData);

    if (response) {
      if (onRequestCreated) {
        onRequestCreated(); // calls the parent refresh
      }

      resetForm();
      setOpenCreateModal(false);
    }
  };

  // Reset form to initial state
  const resetForm = () => {
    if (isMountedRef.current) {
      setFormValues(initialFormState);
      setSelectedKeywords([]);
      setKeywordSearch('');
      setShowSuggestions(false);
      setErrors({});
    }
  };

  // Close modal and reset form
  const handleClose = () => {
    resetForm();
    setOpenCreateModal(false);
  };

  return (
    <CommonModal
      isOpen={openCreateModal}
      setIsOpen={handleClose}
      removePadding
      classStyle='w-[90%] h-auto max-h-[80vh] rounded-md overflow-y-auto'
    >
      <div className='p-6'>
        <h2 className='text-xl font-semibold mb-4'>Create New Request</h2>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <TextField
            label='Request Name'
            name='requestName'
            variant='outlined'
            fullWidth
            type='text'
            value={formValues.requestName}
            onChange={handleChange}
            error={!!errors.requestName}
          />

          <FormControl variant='outlined' fullWidth error={!!errors.platform}>
            <InputLabel id='platform-label'>Platform</InputLabel>
            <Select
              labelId='platform-label'
              name='platform'
              value={formValues.platform}
              onChange={handleChange}
              label='Platform'
            >
              <MenuItem value='LinkedIn'>LinkedIn</MenuItem>
              <MenuItem value='Naukri'>Naukri</MenuItem>
            </Select>
            {errors.platform && (
              <p className='text-red-500 text-xs mt-1'>{errors.platform}</p>
            )}
          </FormControl>

          {/* Type Selection */}
          <FormControl variant='outlined' fullWidth error={!!errors.type}>
            <InputLabel id='type-label'>Type</InputLabel>
            <Select
              labelId='type-label'
              name='type'
              value={formValues.type}
              onChange={handleChange}
              label='Type'
            >
              <MenuItem value='Cron'>Cron</MenuItem>
              <MenuItem value='OneTime'>One Time</MenuItem>
            </Select>
            {errors.type && (
              <p className='text-red-500 text-xs mt-1'>{errors.type}</p>
            )}
          </FormControl>

          {formValues.type === 'Cron' && (
            <TextField
              label='Frequency (in hours)'
              name='frequency'
              variant='outlined'
              fullWidth
              type='text'
              value={formValues.frequency}
              onChange={(e) => {
                const value = e.target.value;

                // Show error if pasted/typed invalid value
                if (value === '' || /^[0-9]*$/.test(value)) {
                  setFormValues((prev) => ({
                    ...prev,
                    frequency: value,
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    frequency: '',
                  }));
                } else {
                  setErrors((prev) => ({
                    ...prev,
                    frequency: 'Only positive integers are allowed',
                  }));
                }
              }}
              onKeyDown={(e) => {
                const allowedKeys = [
                  'Backspace',
                  'ArrowLeft',
                  'ArrowRight',
                  'Tab',
                  'Delete',
                ];

                if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                  e.preventDefault();
                  setErrors((prev) => ({
                    ...prev,
                    frequency: 'Only Integer Values are allowed',
                  }));
                }
              }}
              error={!!errors.frequency}
              helperText={
                errors.frequency || 'How often to run this request (in hours)'
              }
            />
          )}

          <div className='col-span-2'>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              Keywords
              <span className='text-sm text-gray-500 ml-2'>
                (Search and select existing keywords or add new ones)
              </span>
            </label>

            {selectedKeywords.length > 0 && (
              <div className='mb-3 flex flex-wrap gap-2'>
                {selectedKeywords.map((keyword) => (
                  <Chip
                    key={keyword.id}
                    label={keyword.name}
                    onDelete={() => handleRemoveKeyword(keyword)}
                    deleteIcon={<CloseIcon />}
                    color={keyword.isNew ? 'secondary' : 'primary'}
                    variant='outlined'
                    size='small'
                  />
                ))}
              </div>
            )}

            <div className='relative'>
              <TextField
                fullWidth
                variant='outlined'
                placeholder='Search keywords or type to add new ones...'
                value={keywordSearch}
                onChange={handleKeywordSearchChange}
                onFocus={() => setShowSuggestions(keywordSearch.length > 0)}
                error={!!errors.keywords}
                helperText={errors.keywords}
                InputProps={{
                  endAdornment: keywordLoading ? (
                    <CircularProgress color='inherit' size={20} />
                  ) : null,
                }}
              />

              {/* Suggestions Dropdown */}
              {showSuggestions && (
                <Paper
                  className='absolute z-10 w-full mt-1 max-h-60 overflow-y-auto'
                  elevation={3}
                >
                  <List dense>
                    {keywordSearch.trim() && (
                      <ListItem
                        button
                        onClick={handleAddNewKeyword}
                        className='border-b border-gray-200'
                      >
                        <AddIcon className='mr-2 text-green-600' />
                        <ListItemText
                          primary={`Add "${keywordSearch}" for ${formValues.platform}`}
                          secondary='Create new keyword for selected platform'
                        />
                      </ListItem>
                    )}

                    {/* Existing keyword options */}
                    {keywordOptions
                      .filter(
                        (option) =>
                          !selectedKeywords.find((k) => k.id === option.id) &&
                          option.platform === formValues.platform
                      )
                      .map((option) => (
                        <ListItem
                          button
                          key={option.id}
                          onClick={() => handleAddKeyword(option)}
                        >
                          <ListItemText
                            primary={option.name}
                            secondary={`Platform: ${option.platform}`}
                          />
                        </ListItem>
                      ))}

                    {keywordOptions.length === 0 &&
                      !keywordLoading &&
                      keywordSearch.length > 0 && (
                        <ListItem>
                          <ListItemText primary='No keywords found for selected platform' />
                        </ListItem>
                      )}
                  </List>
                </Paper>
              )}
            </div>

            {selectedKeywords.length > 0 && (
              <div className='mt-2'>
                <p className='text-xs text-gray-500'>
                  Selected: {selectedKeywords.length} keyword(s)
                  {selectedKeywords.filter((k) => k.isNew).length > 0 && (
                    <span className='text-orange-600 ml-2'>
                      ({selectedKeywords.filter((k) => k.isNew).length} new)
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className='flex justify-end space-x-4 p-4 bg-gray-50 rounded-b-md'>
        <button
          onClick={handleClose}
          className='px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors'
        >
          Cancel
        </button>

        <button
          onClick={handleCreateRequest}
          disabled={isLoading}
          className={`px-4 py-2 rounded-md transition-colors flex items-center ${
            isLoading
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-brand-primary text-white hover:bg-opacity-90'
          }`}
        >
          {isLoading ? (
            <>
              <LoadingCircleIcon iconStyle='w-5 mr-2 animate-spin' />
              Creating...
            </>
          ) : (
            'Create Request'
          )}
        </button>
      </div>
    </CommonModal>
  );
};

export default CreateRequest;
